/* eslint-disable complexity */
import React, {useRef, useState, useEffect, useContext} from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {isEqual} from 'lodash';
import {Checkbox} from '@homeaway/react-form-components';
import {APP_NAMES} from '../../constants';
import SearchAreaButton from '../SearchAreaButton/SearchAreaButton';
import {CarouselIconDefinitions} from '../Carousel/Carousel';
import {
    LabelsPropType,
    MapApiConfigOptionsPropType,
    MarkerPropType,
    ThingsOfInterestPropType,
} from '../../utils/proptypes';
import {
    triggerEdapEventWithContext,
    GENERIC_EVENT,
    EDAP_EVENTS,
} from '../../utils/edapHelper';
import {TrackingContext} from '@homeaway/react-event-tracking';
import MapContext from '../../context/MapContext';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import withDOM from '../withDOM/withDOM';
import {updateMarkers, getInfoWindowOptions} from '../Marker/UitkMarker';
import OverlayContent from '../OverlayContent/OverlayContent';
import GoogleMapWrapper from '../GoogleMapWrapper/GoogleMapWrapper';

import './Map.less';

const Map = ({
    apiConfigOptions,
    bounds,
    disableSearchAreaButton,
    disableSearchOnMove,
    enableSearchOnMove,
    isLoading,
    labels,
    onIdle,
    onMapLoaded,
    setMapMoveState,
    showAutoSearchCheckBox,
    shouldSearchOnMove,
    showMapSearchButton,
    showSearchAreaButton,
    thingsOfInterest,
    hoveredMarker,
    selectedMarker,
    ...props
}) => {
    const googleRef = useRef(null);

    let resetMarkersTimer;
    const mapTimeout = 400;
    const [markers, setMarkers] = useState(props.markers);
    const context = useContext(TrackingContext);
    const [userSelectedMarker, setUserSelectedMarker] =
        useState(selectedMarker);
    const [previouslyHoveredMarker, setPreviouslyHoveredMarker] = useState();
    const [overlayMapOptions, setOverlayMapOptions] = useState();
    const [mapZoom, setMapZoom] = useState();
    const [mapBounds, setMapBounds] = useState();
    const [initialMapBounds, setInitialMapBounds] = useState();
    const [shouldCallMapPan, setShouldCallMapPan] = useState(true);

    const createTOIOverlay = (markerId) => {
        if (
            googleRef &&
            googleRef.current &&
            googleRef.current.mapRef &&
            googleRef.current.mapRef.current
        ) {
            const infoWindowOptions = overlayMapOptions.get(markerId);
            const {name, distance} = infoWindowOptions || {};
            const map = googleRef.current.mapRef.current.map();
            if (map) {
                if (name && distance) {
                    const wrappingElement = document.createElement('div');
                    const popupContent = (
                        <OverlayContent
                            name={name}
                            distance={distance}
                            labels={props.labels}
                        />
                    );
                    ReactDOM.render(popupContent, wrappingElement);
                    map.setPopupContent(markerId, 'place', wrappingElement);
                    map.selectFeature(markerId, 'place');
                } else {
                    map.setPopupContent(markerId, 'place', null);
                    map.clearSelectedFeature();
                }
            }
        }
    };

    const selectHoveredTOI = (hoveredMarkerId, prevHoveredMarker) => {
        if (
            googleRef &&
            googleRef.current &&
            googleRef.current.mapRef &&
            googleRef.current.mapRef.current
        ) {
            const map = googleRef.current.mapRef.current.map();
            if (map) {
                if (!hoveredMarkerId || hoveredMarker !== prevHoveredMarker)
                    map.markFeatureOut(prevHoveredMarker, 'place');
                map.markFeatureOver(hoveredMarkerId, 'place');
            }
        }
        return;
    };

    useEffect(() => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            if (
                googleRef &&
                googleRef.current &&
                googleRef.current.mapRef &&
                googleRef.current.mapRef.current
            ) {
                const map = googleRef.current.mapRef.current.map();
                if (map && map.fitBounds && bounds) {
                    map.fitBounds(
                        [bounds.swLat, bounds.swLong],
                        [bounds.neLat, bounds.neLong],
                        false,
                        '0'
                    );
                }
            }
        }
    }, [bounds]);

    useEffect(() => {
        if (apiConfigOptions.appName !== APP_NAMES.SERP) {
            if (
                googleRef &&
                googleRef.current &&
                googleRef.current.mapRef &&
                googleRef.current.mapRef.current
            ) {
                if (initialMapBounds) {
                    const map = googleRef.current.mapRef.current.map();
                    if (map) {
                        map.fitBounds(
                            initialMapBounds.sw,
                            initialMapBounds.ne,
                            false,
                            '0'
                        );
                    }
                }
            }
        }
    }, [hoveredMarker, selectedMarker, thingsOfInterest]);

    useEffect(() => {
        selectHoveredTOI(hoveredMarker, previouslyHoveredMarker);
        setPreviouslyHoveredMarker(hoveredMarker);
    }, [hoveredMarker]);

    useEffect(() => {
        setUserSelectedMarker(selectedMarker);
        if (typeof overlayMapOptions !== 'undefined') {
            createTOIOverlay(selectedMarker);
        }
    }, [selectedMarker]);

    useEffect(() => {
        const {appName} = apiConfigOptions;
        const _markers =
            appName === APP_NAMES.PDP ? thingsOfInterest : props.markers;
        const newMarkers = updateMarkers(
            _markers,
            appName,
            hoveredMarker,
            userSelectedMarker
        );
        setOverlayMapOptions(getInfoWindowOptions(newMarkers));
        setMarkers(newMarkers);
    }, [props.markers, thingsOfInterest, hoveredMarker, userSelectedMarker]);

    const handleMapLoaded = (map) => {
        if (
            !initialMapBounds &&
            googleRef &&
            googleRef.current &&
            googleRef.current.mapRef &&
            googleRef.current.mapRef.current
        ) {
            const egMaps = googleRef.current.mapRef.current.map();
            if (egMaps) setInitialMapBounds(egMaps.getBounds());
        }

        triggerEdapEventWithContext(context, EDAP_EVENTS.MAP_PRESENTED, {
            action_location: apiConfigOptions.appName,
        });

        if (typeof onMapLoaded === 'function') {
            onMapLoaded(map);
        }
    };

    const getBoundsForSerp = ({ne, sw}) => ({
        neLat: Array.isArray(ne) ? ne[0] : ne.lat,
        neLong: Array.isArray(ne) ? ne[1] : ne.lng,
        swLat: Array.isArray(sw) ? sw[0] : sw.lat,
        swLong: Array.isArray(sw) ? sw[1] : sw.lng,
    });

    const onMapMove = (currentBounds, currentZoom) => {
        const {updateMap} = props;
        /* Note: auto update happens here in this updateMap action */
        if (
            !isEqual(currentBounds, mapBounds) ||
            !isEqual(currentZoom, mapZoom)
        ) {
            const boundsForSerp = getBoundsForSerp(currentBounds);
            if (typeof updateMap === 'function') {
                updateMap(boundsForSerp, Math.ceil(currentZoom));
            }
        }
        setMapBounds(currentBounds);
        setMapZoom(currentZoom);
    };

    const onMapUpdated = (newBounds, zoomLevel) => {
        onMapMove(newBounds, zoomLevel);
    };

    const handleIdle = () => {
        if (typeof onIdle === 'function') {
            onIdle();
        }
    };

    const onIdleChanged = () => {
        if (shouldSearchOnMove) {
            clearTimeout(resetMarkersTimer);
            resetMarkersTimer = setTimeout(() => {
                handleIdle();
            }, mapTimeout);
        } else {
            handleIdle();
        }
    };

    const onZoomChanged = () => {
        if (typeof props.onZoomChanged === 'function') {
            props.onZoomChanged();
        }
    };

    const onZoomIn = (mapCurrentBounds, mapCurrentZoom) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            const zoomtype = 'in';
            triggerEdapEventWithContext(context, 'search.map.zoom', {
                zoomtype,
            });
        } else {
            triggerEdapEventWithContext(
                context,
                EDAP_EVENTS.MAP_ZOOM_IN_SELECTED,
                {
                    action_location: apiConfigOptions.appName,
                }
            );
        }
        setMapBounds(mapCurrentBounds);
        setMapZoom(mapCurrentZoom);
    };

    const onZoomOut = (mapCurrentBounds, mapCurrentZoom) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            const zoomtype = 'out';
            triggerEdapEventWithContext(context, 'search.map.zoom', {
                zoomtype,
            });
        } else {
            triggerEdapEventWithContext(
                context,
                EDAP_EVENTS.MAP_ZOOM_OUT_SELECTED,
                {
                    action_location: apiConfigOptions.appName,
                }
            );
        }
        setMapBounds(mapCurrentBounds);
        setMapZoom(mapCurrentZoom);
    };

    const onDragStart = () => {
        clearTimeout(resetMarkersTimer);
        if (typeof showMapSearchButton === 'function') {
            showMapSearchButton();
        }
        if (typeof setMapMoveState === 'function') {
            setMapMoveState(true);
        }
        if (typeof props.onDragStart === 'function') {
            props.onDragStart();
        }
    };

    const onDragEnd = (mapCurrentBounds, mapCurrentZoom) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, 'search.map.move', {
                highfrequency: 'true',
            });
        }
        triggerEdapEventWithContext(context, EDAP_EVENTS.MAP_MOVE_SELECTED, {
            action_location: apiConfigOptions.appName,
        });
        if (typeof setMapMoveState === 'function') {
            setMapMoveState(false);
        }
        if (typeof props.onDragEnd === 'function') {
            props.onDragEnd();
        }
        setMapBounds(mapCurrentBounds);
        setMapZoom(mapCurrentZoom);
    };

    const handleSerpSearchEvent = () => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, GENERIC_EVENT, {
                eventcategory: 'search',
                eventaction: 'map search button click',
            });
        }
    };

    const handleSearch = () => {
        const {mapSearch, resetSearchButton, syncBounds} = props;
        if (
            googleRef &&
            googleRef.current &&
            googleRef.current.mapRef &&
            googleRef.current.mapRef.current
        ) {
            const map = googleRef.current.mapRef.current.map();
            if (map) {
                const currentBounds = map.getBounds();
                const boundsForSerp = getBoundsForSerp(currentBounds);
                handleSerpSearchEvent();

                if (typeof resetSearchButton === 'function') {
                    resetSearchButton();
                }
                if (currentBounds && boundsForSerp) {
                    if (typeof mapSearch === 'function') {
                        mapSearch({
                            ...boundsForSerp,
                            zoom: Math.ceil(mapZoom),
                        });
                    }
                    if (typeof syncBounds === 'function') {
                        syncBounds(boundsForSerp);
                    }
                }
            }
        }
    };

    const handleSerpAutoSearchEvent = (event) => {
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            triggerEdapEventWithContext(context, GENERIC_EVENT, {
                eventaction: 'auto search checkbox',
                eventlabel: event.currentTarget.checked
                    ? 'checked'
                    : 'unchecked',
            });
        }
    };

    const handleMapSearch = () => {
        const {mapSearch, zoom} = props;
        if (
            googleRef &&
            googleRef.current &&
            googleRef.current.mapRef &&
            googleRef.current.mapRef.current
        ) {
            const map = googleRef.current.mapRef.current.map();
            if (map) {
                const currentBounds = map.getBounds();
                const boundsForSerp = getBoundsForSerp(currentBounds);
                if (typeof mapSearch === 'function') {
                    mapSearch({
                        ...boundsForSerp,
                        zoom,
                    });
                }
            }
        }
    };

    const handleAutoSearchCheck = (event) => {
        if (!event || !event.currentTarget) {
            return;
        }
        if (event.currentTarget.checked) {
            if (typeof enableSearchOnMove === 'function') {
                enableSearchOnMove();
            }
            handleMapSearch();
        } else if (typeof disableSearchOnMove === 'function') {
            disableSearchOnMove();
        }
        handleSerpAutoSearchEvent(event);
    };

    const onMapClick = () => {
        if (apiConfigOptions.appName === APP_NAMES.LANDINGAPP) {
            googleRef?.current?.mapRef?.current?.map()?.clearSelectedFeature();
        }
        setUserSelectedMarker(null);
        if (typeof props.onToiClick === 'function') {
            props.onToiClick(null);
        }
    };

    const onMarkerClick = (clickedMarker) => {
        const uuid = clickedMarker.id;
        if (apiConfigOptions.appName === APP_NAMES.SERP) {
            setShouldCallMapPan(false);
            const newMarkers = [...markers];
            setMarkers(
                newMarkers.map((marker) => {
                    const markerUuid = `${marker.lat}${marker.lng}`;
                    if (markerUuid === uuid) {
                        marker.isVisited = true;
                    }
                    return marker;
                })
            );
        }
        if (apiConfigOptions.appName === APP_NAMES.LANDINGAPP) {
            googleRef?.current?.mapRef?.current
                ?.map()
                ?.markFeatureOut(userSelectedMarker, 'place');
        }
        if (typeof props.onMarkerClick === 'function') {
            props.onMarkerClick(uuid);
        }
        if (typeof props.onToiClick === 'function') {
            props.onToiClick(uuid);
        }
        setUserSelectedMarker(uuid);
    };

    const onMarkerChange = () => {
        if (typeof props.onMarkerChange === 'function') {
            props.onMarkerChange();
        }
    };

    const onHoverChange = (marker, setPopupContent, action) => {
        if (apiConfigOptions.appName === APP_NAMES.LANDINGAPP) {
            if (
                googleRef &&
                googleRef.current &&
                googleRef.current.mapRef &&
                googleRef.current.mapRef.current &&
                action === 'mouseLeave'
            ) {
                if (userSelectedMarker !== marker.id) {
                    setPopupContent(null);
                    const infoWindowOptions =
                        overlayMapOptions.get(userSelectedMarker);
                    const {content} = infoWindowOptions || {};
                    if (content) {
                        const popupcontent = (
                            <div className={content.props.className}>
                                <a
                                    className={
                                        content.props.children.props.className
                                    }
                                    href={content.props.children.props.href}
                                >
                                    {content.props.children.props.children}
                                </a>
                            </div>
                        );
                        const wrappingElement = document.createElement('div');
                        ReactDOM.render(popupcontent, wrappingElement);
                        setPopupContent(wrappingElement);
                        googleRef.current.mapRef.current
                            .map()
                            ?.selectFeature(userSelectedMarker, 'place');
                    }
                }
            }
        }
        if (typeof props.onHoverchange === 'function') {
            props.onHoverChange(marker, action);
        }
    };

    return (
        <MapContext.Provider
            value={{
                appName: apiConfigOptions.appName,
                apiKey: apiConfigOptions.apiKey,
                isMobile: props.isMobile,
                labels: props.labels,
            }}
        >
            <CarouselIconDefinitions />
            <GoogleMapWrapper
                {...props}
                ref={googleRef}
                zoom={props.zoom}
                bounds={bounds}
                labels={labels}
                onIdle={onIdleChanged}
                onZoomChanged={onZoomChanged}
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                apiConfigOptions={apiConfigOptions}
                onMapClick={onMapClick}
                onMapLoaded={handleMapLoaded}
                onMarkerChange={onMarkerChange}
                onMarkerClick={onMarkerClick}
                markers={markers}
                overlayMapOptions={overlayMapOptions}
                onHoverChange={onHoverChange}
                userSelectedMarker={userSelectedMarker}
                onMapUpdated={onMapUpdated}
                shouldCallMapPan={shouldCallMapPan}
                setShouldCallMapPan={setShouldCallMapPan}
            >
                {showAutoSearchCheckBox && (
                    <Checkbox
                        className={classnames(
                            'Map__control',
                            'Map__control--auto-search'
                        )}
                        data-wdio={'Map__autoSearch'}
                        key={'auto_search'}
                        name={'auto_search'}
                        checked={shouldSearchOnMove}
                        onChange={handleAutoSearchCheck}
                        label={labels.autosearch}
                        size={'sm'}
                        inline
                    />
                )}
                <SearchAreaButton
                    isVisible={showSearchAreaButton}
                    isDisabled={disableSearchAreaButton}
                    onClick={handleSearch}
                    isLoading={isLoading}
                />
            </GoogleMapWrapper>
        </MapContext.Provider>
    );
};

Map.propTypes = {
    /** Required map api configuration */
    apiConfigOptions: MapApiConfigOptionsPropType.isRequired,
    /** Array for markers you want to generate */
    markers: PropTypes.arrayOf(MarkerPropType.isRequired),
    /** Show the Auto search checkbox */
    showAutoSearchCheckBox: PropTypes.bool,
    /** Labels for entire map component. Only required if you are using custom features */
    labels: LabelsPropType,
    /** Boolean to determine to initiate search on map move. Sets to true only if Auto Search checkbox is checked  */
    shouldSearchOnMove: PropTypes.bool,
    /** Shows the Search Area Button. Button is only shown when Auto Search checkbox is unticked. */
    showSearchAreaButton: PropTypes.bool,
    /** Disables the Search Area Button */
    disableSearchAreaButton: PropTypes.bool,
    /** Boolean that determines if the map is loading */
    isLoading: PropTypes.bool,
    /** Boolean that determines to call updateMap method or not */
    allowMapUpdate: PropTypes.bool,
    /** Function that sets new bounds and zoom*/
    updateMap: PropTypes.func,
    /** Function that sets the mapMoveState to true or false on mapDrag */
    setMapMoveState: PropTypes.func,
    /** Function that sets the booleans shouldShowSearchButton and disableSearchAreaButton   */
    showMapSearchButton: PropTypes.func,
    /** Function that sets the bound and new zoom values*/
    mapSearch: PropTypes.func,
    /** Function that reset search button*/
    resetSearchButton: PropTypes.func,
    /** Function to sync new bounds */
    syncBounds: PropTypes.func,
    /** Function that sets the boolean shouldSearchOnMove to true */
    enableSearchOnMove: PropTypes.func,
    /** Function that sets the boolean shouldSearchOnMove to false */
    disableSearchOnMove: PropTypes.func,
    /** Callback fired when Map is in idle state */
    onIdle: PropTypes.func,
    /** Callback fired when zoom factor has changed */
    onZoomChanged: PropTypes.func,
    /** Callback fired when user has started to drag the map */
    onDragStart: PropTypes.func,
    /** Callback fired when user has released left click to end drag of map */
    onDragEnd: PropTypes.func,
    /** Set a marker to be selected */
    selectedMarker: PropTypes.string,
    /** Set a marker to be hovered */
    hoveredMarker: PropTypes.string,
    /** Callback fired when a marker is clicked */
    onMarkerClick: PropTypes.func,
    /** Callbac fired when a marker is hovered */
    onMarkerHover: PropTypes.func,
    /** Callback fired when the mouse has left hovered marker */
    onMarkerHoverLeave: PropTypes.func,
    /** Things of interest array for PDP application. Undefined for everything else */
    thingsOfInterest: PropTypes.arrayOf(ThingsOfInterestPropType),
    /** This prop will be used for SERP map expansion */
    shouldMapBeExpanded: PropTypes.bool,
    /** Fired when the map has been loaded  */
    onMapLoaded: PropTypes.func,
};

Map.defaultProps = {
    markers: [],
    showAutoSearchCheckBox: false,
    disableSearchAreaButton: false,
    selectedMarker: null,
    hoveredMarker: null,
    shouldMapBeExpanded: false,
};

export default withDOM(determineIfMobile(Map));
